import React, { useState } from 'react';
import { ChevronDown, X, Loader2 } from 'lucide-react';
import { useCreateHellocian } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';

const HellocianDetailsForm = () => {
    const setAlert = useAlert();
    const { mutateAsync: createHellocian, isPending } = useCreateHellocian();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        bio: '',
        skill: '',
        keywords: [],
        markAsActive: true,
        notifyForNotifications: true
    });

    const [showSkillDropdown, setShowSkillDropdown] = useState(false);
    const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
    const [keywordSearch, setKeywordSearch] = useState('');

    const skills = [
        'UI/UX Design',
        'Web Development',
        'Mobile App Development',
        'Graphic Design',
        'Digital Marketing',
        'Content Writing',
        'Video Editing',
        'Photography',
        'Social Media Management',
        'SEO Specialist'
    ];

    const availableKeywords = [
        'Software Engineer',
        'Developer',
        'Marketer',
        'Designer',
        'Frontend Developer',
        'Backend Developer',
        'Full Stack Developer',
        'Product Manager',
        'Data Scientist',
        'DevOps Engineer',
        'Mobile Developer',
        'UI Designer',
        'UX Designer',
        'Graphic Designer',
        'Digital Marketer',
        'Content Creator',
        'SEO Specialist',
        'Social Media Manager',
        'Project Manager',
        'Business Analyst',
        'Quality Assurance',
        'Photographer',
        'Video Editor',
        'Copywriter',
        'Brand Manager'
    ];

    const filteredKeywords = availableKeywords.filter(keyword =>
        keyword.toLowerCase().includes(keywordSearch.toLowerCase()) &&
        !formData.keywords.includes(keyword)
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleToggle = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSkillSelect = (skill) => {
        setFormData(prev => ({
            ...prev,
            skill: skill
        }));
        setShowSkillDropdown(false);
    };

    const handleKeywordSelect = (keyword) => {
        setFormData(prev => ({
            ...prev,
            keywords: [...prev.keywords, keyword]
        }));
        setKeywordSearch('');
        setShowKeywordDropdown(false);
    };

    const removeKeyword = (keywordToRemove) => {
        setFormData(prev => ({
            ...prev,
            keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
        }));
    };

    const handleKeywordSearchChange = (e) => {
        setKeywordSearch(e.target.value);
        setShowKeywordDropdown(true);
    };

    const handleSubmit = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber) {
            setAlert({ type: 'error', message: 'First Name, Last Name, Email, and Phone Number are required' });
            return;
        }

        try {
            await createHellocian({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phoneNumber,
                bio: formData.bio,
                skills: [formData.skill].filter(Boolean),
                keywords: formData.keywords
            });
            setAlert({ type: 'success', message: 'Hellocian invited successfully!' });
            // Reset form
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                bio: '',
                skill: '',
                keywords: [],
                markAsActive: true,
                notifyForNotifications: true
            });
        } catch (err) {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to invite Hellocian' });
        }
    };

    const Toggle = ({ checked, onChange, label }) => (
        <div className="flex items-center gap-4 py-3">
            <div
                onClick={onChange}
                className={`relative inline-flex h-6 w-12 cursor-pointer rounded-full transition-colors duration-200 ease-in-out ${checked ? 'bg-[#1FA860]' : 'bg-gray-300'
                    }`}
            >
                <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out ${checked ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}
                />
            </div>
            <span className="text-[#232323] dark:text-gray-300 font-regular">{label}</span>
        </div>
    );

    return (
        <div className="bg-white dark:bg-[#141414] p-6 rounded-lg border border-gray-200 dark:border-gray-800 transition-colors duration-200">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-[#32353c] pb-4 mb-6">
                <h2 className="text-xl font-semibold text-[#4B718F] dark:text-blue-400">Hellocian Details</h2>
            </div>

            <div className="space-y-6">
                {/* Name Fields Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            First Name
                        </label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Last Name
                        </label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors"
                        />
                    </div>
                </div>

                {/* Email and Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter email address"
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors"
                        />
                    </div>
                </div>

                {/* Skill and Bio Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Skill
                        </label>
                        <div
                            onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 flex items-center justify-between cursor-pointer transition-colors"
                        >
                            <span className={formData.skill ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
                                {formData.skill || 'Please select'}
                            </span>
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showSkillDropdown ? 'rotate-180' : ''}`} />
                        </div>

                        {showSkillDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                {skills.map((skill, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleSkillSelect(skill)}
                                        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-[#32353c] last:border-b-0"
                                    >
                                        {skill}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Bio (Description)
                        </label>
                        <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            placeholder="Brief bio about the Hellocian"
                            rows="1"
                            className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors resize-none"
                        />
                    </div>
                </div>

                {/* Keywords */}
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Keywords
                    </label>

                    {/* Selected Keywords Tags */}
                    {formData.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {formData.keywords.map((keyword, index) => (
                                <div
                                    key={index}
                                    className="inline-flex items-center bg-[#174568] dark:bg-blue-600 text-white px-3 py-1 rounded-full text-sm shadow-sm"
                                >
                                    <span>{keyword}</span>
                                    <button
                                        onClick={() => removeKeyword(keyword)}
                                        className="ml-2 hover:bg-[#0f3a4f] dark:hover:bg-blue-700 rounded-full p-0.5 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Search Input */}
                    <input
                        type="text"
                        value={keywordSearch}
                        onChange={handleKeywordSearchChange}
                        onFocus={() => setShowKeywordDropdown(true)}
                        placeholder="Search and select keywords"
                        className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] dark:border-[#32353c] bg-[#F2F4F8] dark:bg-[#1d2026] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-b-[#174568] dark:focus:border-b-blue-500 transition-colors"
                    />

                    {/* Keywords Dropdown */}
                    {showKeywordDropdown && filteredKeywords.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                            {filteredKeywords.map((keyword, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleKeywordSelect(keyword)}
                                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-[#32353c] last:border-b-0"
                                >
                                    {keyword}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

              

                {/* Save Button */}
                <div className="flex justify-end pt-6">
                    <button
                        onClick={handleSubmit}
                        disabled={isPending}
                        className="px-8 py-3 bg-[#174568] text-white rounded-lg hover:bg-[#0f3a4f] transition-colors font-medium flex items-center justify-center gap-2"
                    >
                        {isPending ? <Loader2 className="animate-spin" size={20} /> : 'Save'}
                    </button>
                </div>
            </div>

            {/* Click outside to close dropdowns */}
            {(showSkillDropdown || showKeywordDropdown) && (
                <div
                    className="fixed inset-0 z-5"
                    onClick={() => {
                        setShowSkillDropdown(false);
                        setShowKeywordDropdown(false);
                    }}
                />
            )}
        </div>
    );
};

export default HellocianDetailsForm;